import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Mic, Send, MicOff, Lightbulb, Zap, Clock } from 'lucide-react';
import { ProcessingCommand } from '../types/media';

interface CommandInterfaceProps {
  onCommand: (command: string) => void;
  currentCommand: string;
  setCurrentCommand: (command: string) => void;
  isProcessing: boolean;
  commandHistory: ProcessingCommand[];
  fileType?: 'audio' | 'video';
}

const CommandInterface: React.FC<CommandInterfaceProps> = ({
  onCommand,
  currentCommand,
  setCurrentCommand,
  isProcessing,
  commandHistory,
  fileType
}) => {
  const [isListening, setIsListening] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const recognition = useRef<SpeechRecognition | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const suggestions = fileType === 'audio' ? [
    "Cut first 10 seconds",
    "Change pitch to robotic",
    "Add reverb effect",
    "Reduce background noise",
    "Make voice deeper",
    "Speed up by 1.5x",
    "Add echo",
    "Convert to female voice"
  ] : [
    "Make it black and white",
    "Add text 'Hello World'",
    "Cut from 0:30 to 1:30",
    "Apply cinematic filter",
    "Slow down to 0.5x speed",
    "Add fade transition",
    "Replace background with cityscape",
    "Make it vintage style"
  ];

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [currentCommand]);

  const handleSendCommand = () => {
    if (currentCommand.trim() && !isProcessing) {
      onCommand(currentCommand.trim());
      setCurrentCommand('');
      setShowSuggestions(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendCommand();
    }
  };

  const startVoiceRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = false;
      recognition.current.interimResults = true;
      recognition.current.lang = 'en-US';

      recognition.current.onstart = () => {
        setIsListening(true);
      };

      recognition.current.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setCurrentCommand(transcript);
      };

      recognition.current.onerror = () => {
        setIsListening(false);
      };

      recognition.current.onend = () => {
        setIsListening(false);
      };

      recognition.current.start();
    }
  };

  const stopVoiceRecognition = () => {
    if (recognition.current) {
      recognition.current.stop();
    }
    setIsListening(false);
  };

  const applySuggestion = (suggestion: string) => {
    setCurrentCommand(suggestion);
    setShowSuggestions(false);
    textareaRef.current?.focus();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-800 bg-black">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-5 h-5" />
            <h3 className="text-lg font-semibold">AI Command Center</h3>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowSuggestions(!showSuggestions)}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            title="Show command suggestions"
          >
            <Lightbulb className={`w-4 h-4 ${showSuggestions ? 'text-yellow-400' : 'text-gray-400'}`} />
          </motion.button>
        </div>
      </div>

      <div className="p-4">
        {/* Command History */}
        <AnimatePresence>
          {commandHistory.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4"
            >
              <h4 className="text-sm font-medium text-gray-400 mb-2 flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>Recent Commands</span>
              </h4>
              <div className="space-y-2 max-h-24 overflow-y-auto">
                {commandHistory.slice(-3).map((cmd, index) => (
                  <motion.div
                    key={cmd.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-xs text-gray-300 bg-gray-800 p-2 rounded border-l-2 border-green-500 cursor-pointer hover:bg-gray-700 transition-colors"
                    onClick={() => setCurrentCommand(cmd.command)}
                  >
                    <div className="font-medium">{cmd.command}</div>
                    <div className="text-gray-500 mt-1">Type: {cmd.type}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Command Suggestions */}
        <AnimatePresence>
          {showSuggestions && suggestions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4"
            >
              <h4 className="text-sm font-medium text-gray-400 mb-2">
                {fileType === 'audio' ? 'Audio' : 'Video'} Commands
              </h4>
              <div className="grid grid-cols-1 gap-1 max-h-32 overflow-y-auto">
                {suggestions.map((suggestion, index) => (
                  <motion.button
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.02, x: 5 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => applySuggestion(suggestion)}
                    className="text-left text-xs text-gray-400 hover:text-white p-2 rounded hover:bg-gray-800 transition-colors border border-transparent hover:border-gray-700"
                    disabled={isProcessing}
                  >
                    {suggestion}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Command Input */}
        <div className="space-y-3">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={currentCommand}
              onChange={(e) => setCurrentCommand(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Type your ${fileType || 'media'} editing command here...`}
              className="w-full bg-black border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:border-white focus:outline-none resize-none min-h-[80px] max-h-[120px]"
              disabled={isProcessing}
            />
            {isListening && (
              <div className="absolute top-2 right-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              </div>
            )}
          </div>

          <div className="flex space-x-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={isListening ? stopVoiceRecognition : startVoiceRecognition}
              className={`p-3 rounded-lg border transition-all duration-200 ${
                isListening
                  ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-600/25'
                  : 'border-gray-700 hover:border-gray-600 hover:bg-gray-800'
              }`}
              disabled={isProcessing}
              title={isListening ? 'Stop voice input' : 'Start voice input'}
            >
              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSendCommand}
              disabled={!currentCommand.trim() || isProcessing}
              className="flex-1 bg-white text-black px-4 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
            >
              <Send className="w-5 h-5" />
              <span>{isProcessing ? 'Processing...' : 'Apply Command'}</span>
            </motion.button>
          </div>
        </div>

        {/* Processing Status */}
        <AnimatePresence>
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-4 p-3 bg-yellow-900 bg-opacity-30 border border-yellow-700 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full"
                />
                <span className="text-yellow-400 text-sm font-medium">
                  AI is processing your command...
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Command Examples */}
        <div className="mt-4 pt-4 border-t border-gray-800">
          <h4 className="text-xs font-medium text-gray-500 mb-2">Example Commands:</h4>
          <div className="text-xs text-gray-600 space-y-1">
            {fileType === 'audio' ? (
              <>
                <p>• "Remove silence from audio"</p>
                <p>• "Increase bass by 30%"</p>
                <p>• "Make voice deeper"</p>
                <p>• "Add echo effect"</p>
              </>
            ) : (
              <>
                <p>• "Cut from 0:30 to 1:30"</p>
                <p>• "Add text 'Hello' at 0:05"</p>
                <p>• "Make it cinematic"</p>
                <p>• "Slow down by 50%"</p>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CommandInterface;